import { LoadingArea, PieChart, type PieChartDataItem } from '@consigli/facade';
import {
  useGetDashboardDataQuery,
  usePackage,
  usePackageId,
  useProjectId,
  usePublishedPolling,
} from '@consigli/hooks';
import { ConflictCategory, ConflictType, FindingStatus } from '@consigli/types';
import clsx from 'clsx';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AdminBanner } from '@/components/admin-banner';
import { WaitingForAnalysis } from '@/components/waiting-for-analysis';
import { useBidReviewContractors } from '@/features/package-creation/bid-review/use-bid-review-contractors';
import { AnalysisPreviewOverlay } from '@/features/package-creation/components/analysis-preview/analysis-preview-overlay';
import { WorkspaceProvider } from '@/layouts/workspace-layout/use-workspace-context';
import { WorkspaceLayout } from '@/layouts/workspace-layout/workspace-layout';
import { useDemoMode } from '@/util/demo-mode';

import { DashboardCard } from '../insight/dashboard/dashboard-card';
import {
  buildDashboardConflictSections,
  conflictSectionReactKey,
  conflictSectionTitleKey,
  type DashboardConflictCard,
  type DashboardConflictSection,
} from '../insight/dashboard/dashboard-conflict-sections';
import { DashboardSearch } from '../insight/dashboard/dashboard-search';

const OfferDashboardContent: FC<{ isPublished: boolean }> = ({ isPublished }) => {
  const { t } = useTranslation();
  const projectId = useProjectId();
  const packageId = usePackageId();
  const { pkg } = usePackage(projectId, packageId);

  const { contractors, isLoading: contractorsLoading } = useBidReviewContractors();

  // Legacy OFFER packages predate per-bid `reference` tagging, so they resolve no
  // contractors. Those still open in this new Bid Review flow, falling back to a
  // package-wide view instead of the per-contractor tabs.
  const hasContractors = contractors.length > 0;

  const [selectedContractor, setSelectedContractor] = useState<string | undefined>();

  // Default to the first bid, and keep the selection valid as blobs load in.
  useEffect(() => {
    if (contractors.length === 0) {
      setSelectedContractor(undefined);
      return;
    }
    setSelectedContractor((current) =>
      current && contractors.includes(current) ? current : contractors[0],
    );
  }, [contractors]);

  // Per-contractor stats are computed on the backend (blobs + findings scoped to
  // the contractor's `reference`), matching the package-wide Insight dashboard.
  // With no contractors (a legacy package) `reference` is left undefined, so the
  // backend returns package-wide stats — the same view the legacy Offer service
  // shows — instead of stranding the package on the "No bids found" state.
  const { data: dashboardData, isFetching: dashboardFetching } = useGetDashboardDataQuery(
    { projectId, packageId, reference: selectedContractor },
    { skip: hasContractors && !selectedContractor },
  );

  const documentStats = dashboardData?.filesCount;

  const piechartData = useMemo<PieChartDataItem[]>(() => {
    const stats = dashboardData?.statusPieChart ?? {};
    return [
      {
        title: t('dashboard.not-relevant'),
        value: stats[FindingStatus.NOT_RELEVANT] ?? 0,
        color: '#CBD5E1',
      },
      { title: t('dashboard.active'), value: stats[FindingStatus.ACTIVE] ?? 0, color: '#475569' },
      {
        title: t('dashboard.processing'),
        value: stats[FindingStatus.PROCESSING] ?? 0,
        color: '#7588A3',
      },
      {
        title: t('dashboard.processed'),
        value: stats[FindingStatus.PROCESSED] ?? 0,
        color: '#ffffff',
      },
    ];
  }, [dashboardData, t]);

  // ADDRESSED marks requirements a bid fully covers — a positive outcome, not a
  // gap to action — so Bid Review hides it from the Gaps dashboard while keeping
  // it available for findings filtering and labels. Drop a section that ends up
  // empty once ADDRESSED is removed.
  const conflictSections = useMemo<DashboardConflictSection[]>(
    () =>
      buildDashboardConflictSections(dashboardData?.conflictTypesCount, false, false)
        .map((section) => ({
          ...section,
          cards: section.cards.filter(
            (card) => Object.keys(card.conflictType)[0] !== ConflictType.ADDRESSED,
          ),
        }))
        .filter((section) => section.cards.length > 0),
    [dashboardData],
  );

  const navToFindingsFiltered = useCallback(
    (conflictCategory: ConflictCategory | number, conflictType?: ConflictType) => {
      const params = new URLSearchParams();
      params.set('conflictCategory', String(conflictCategory));
      if (conflictType) {
        params.set('conflictType', conflictType);
      }
      if (selectedContractor) {
        params.set('reference', selectedContractor);
      }
      return `../findings?${params.toString()}`;
    },
    [selectedContractor],
  );

  const renderConflictCard = useCallback(
    (sectionKey: string, card: DashboardConflictCard) => {
      const { navConflictCategory, conflictType } = card;
      const typeId = Object.keys(conflictType)[0];
      if (!typeId) return null;
      const conflictTypeEnum = ConflictType[typeId as keyof typeof ConflictType] || typeId;
      return (
        <div key={`${sectionKey}-${navConflictCategory}-${typeId}`}>
          <DashboardCard
            title={t([
              `dashboard.conflict-title.${conflictTypeEnum}`,
              'dashboard.conflict-title.__fallback',
            ])}
            body={conflictType[typeId]}
            navigateTo={navToFindingsFiltered(navConflictCategory, conflictTypeEnum)}
          />
        </div>
      );
    },
    [t, navToFindingsFiltered],
  );

  if (contractorsLoading) {
    return <LoadingArea />;
  }

  // Only a package with neither bids nor any documents at all gets the
  // explanatory empty state. A legacy package (no contractors but documents
  // present) falls through to the package-wide view below.
  const showEmptyState =
    !hasContractors && !dashboardFetching && (dashboardData?.filesCount?.all ?? 0) === 0;

  return (
    <div className="px-8">
      <div className="flex flex-col">
        <AdminBanner published={isPublished} />

        {showEmptyState ? (
          <div className="py-10 text-day-neutral-subtle">{t('bid-review.dashboard.no-bids')}</div>
        ) : (
          <>
            {hasContractors && (
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-day-neutral-subtle">
                  {t('bid-review.dashboard.view-contractor')}
                </span>
                {contractors.map((contractor) => (
                  <button
                    key={contractor}
                    type="button"
                    onClick={() => setSelectedContractor(contractor)}
                    className={clsx(
                      'rounded-full px-3 py-1 text-xs font-medium transition-colors',
                      selectedContractor === contractor
                        ? 'bg-day-neutral-dark text-white'
                        : 'bg-day-light-3 text-day-neutral-subtle hover:bg-day-light-4',
                    )}
                  >
                    {contractor}
                  </button>
                ))}
              </div>
            )}

            <div className="flex items-center gap-3 pt-6 pb-8">
              <span className="text-day-neutral-subtle text-3xl font-semibold">
                {selectedContractor ?? pkg?.name}
              </span>
            </div>

            {!documentStats || dashboardFetching ? (
              <LoadingArea />
            ) : (
              <>
                <div className="flex flex-col lg:flex-row justify-between mt-4 gap-4">
                  <div className="flex flex-col basis-7/12 w-full">
                    <div className="flex flex-col md:flex-row gap-8">
                      <DashboardCard
                        title={t('dashboard.analyzed-documents')}
                        body={`${documentStats.processed}/${documentStats.all}`}
                        navigateTo={'../documents'}
                      />
                      <DashboardCard
                        title={t('dashboard.duplicated-documents')}
                        body={documentStats.duplicates}
                        navigateTo={'../documents'}
                      />
                    </div>
                    <DashboardSearch />
                  </div>
                  <PieChart
                    data={piechartData}
                    centerLabel={t('dashboard.pie-findings')}
                    className="bg-day-light-3 rounded-md p-6"
                  />
                </div>

                <div className="flex gap-3 flex-col my-6">
                  {conflictSections.map((section) => {
                    const sectionKey = conflictSectionReactKey(section);
                    return (
                      <div className="w-full" key={sectionKey}>
                        <div className="w-full">
                          <h3 className="text-2xl font-semibold text-day-neutral-subtle py-4">
                            {t(conflictSectionTitleKey(section))}
                          </h3>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 justify-between gap-8">
                          {section.cards.map((card) => renderConflictCard(sectionKey, card))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export const OfferDashboardPage: FC = () => {
  const projectId = useProjectId();
  const packageId = usePackageId();
  const { adminFeaturesEnabled } = useDemoMode();
  const { pkg } = usePackage(projectId, packageId);

  const { isPublished } = usePublishedPolling(
    projectId,
    packageId,
    pkg ? pkg.published : false,
    adminFeaturesEnabled,
  );

  const waitingForAnalysis = !adminFeaturesEnabled && !isPublished;

  return (
    <WorkspaceProvider>
      <WorkspaceLayout hideTabs={waitingForAnalysis}>
        {waitingForAnalysis ? (
          <>
            <WaitingForAnalysis />
            <AnalysisPreviewOverlay />
          </>
        ) : (
          <OfferDashboardContent isPublished={isPublished} />
        )}
      </WorkspaceLayout>
    </WorkspaceProvider>
  );
};
